// Le formateur de valeurs de l'observatoire, extrait de `components.tsx` le 22/09.
//
// Pourquoi il vit maintenant à part : le partage en image doit écrire les MÊMES nombres que
// l'écran — une image qui arrondirait autrement que la tuile qu'elle prétend reproduire
// serait pire qu'une absence d'image. Mais `partage/image.ts` est importé PAR
// `components.tsx`, et lui faire importer `fmt` en retour formait un cycle : deux modules
// qui s'attendent l'un l'autre. Cela fonctionne tant que l'appel est tardif, et casse le
// jour où le découpage en paquets change l'ordre d'initialisation — une panne qui
// n'apparaît qu'en production, jamais en développement.
//
// Un formateur n'a de toute façon rien à faire dans un fichier de composants : il ne rend
// rien. `components.tsx` le ré-exporte, donc les appels existants sont inchangés.

// Exporté (12/08) pour le tableau des écarts de la synthèse : un second formateur aurait
// divergé des arrondis S4-3 actés par Cyrille et Marc le 26/07 — et l'écart affiché doit
// rester reproductible depuis les deux nombres montrés à l'écran.
export const fmt = (v: number | string | null | undefined, unit?: string,
                    decimales?: number): string => {
  if (v === null || v === undefined) return 'N/D'
  if (typeof v === 'number') {
    // `decimales` : précision imposée par le BACKEND quand la règle d'unité ne suffit
    // pas — les taux VOTÉS TFB/TFNB (34,09 % est la valeur délibérée par le conseil,
    // une valeur juridique : « % → 1 déc » l'aurait tronquée). Recette Marc 06/08.
    if (decimales !== undefined && decimales !== null)
      return v.toLocaleString('fr-FR', { maximumFractionDigits: decimales })
    // Arrondis actés S4-3 (Cyrille+Marc 26/07) : % → 1 décimale max ; ‰ et toute unité
    // « pour N » → 2 max. Recette Marc 06/08 : €/hab et € → l'unité (aucune
    // signifiance au centime d'un Σ€/Σhab) ; années → 1 (« 3 ans » écraserait 3,3).
    const u = (unit || '').trim()
    if (u === '%') return v.toLocaleString('fr-FR', { maximumFractionDigits: 1 })
    if (u === '‰' || /pour\s*[\d\s]/.test(u) || /\/\s*[\d\s]/.test(u))
      return v.toLocaleString('fr-FR', { maximumFractionDigits: 2 })
    if (u.startsWith('€/hab') || u === '€')
      return v.toLocaleString('fr-FR', { maximumFractionDigits: 0 })
    if (u.startsWith('années'))
      return v.toLocaleString('fr-FR', { maximumFractionDigits: 1 })
    return v.toLocaleString('fr-FR')
  }
  return v
}

// Écart signé — le SIGNE suit la valeur AFFICHÉE, pas la valeur brute : un écart de
// 0,03 % arrondi à une décimale rendait « (+0) », qui affirme un zéro exact faux
// (revue codex, 06/08). Quand l'arrondi efface l'écart, on l'écrit « ±0 ».
export const fmtDelta = (v: number, unit?: string): string => {
  const s = fmt(v, unit)
  if (/^-?0(?:,0+)?$/.test(s.replace(/\s/g, ''))) return '±0'
  return (v > 0 ? '+' : '') + s
}

// Unité ABRÉGÉE pour les lignes de comparaison et de témoins (recette Marc 05/08,
// `décalage.png`) : les unités longues du train 2 (« % des rés. principales ») se
// répétaient sur CHAQUE ligne d'échelle et de témoin — huit fois par tuile — et
// faisaient zigzaguer les colonnes. L'unité longue ne vit que sur le KPI, qui fixe le
// contexte ; en dessous, « % » suffit.
// Exporté (12/08) pour le tableau des écarts : la valeur de référence y est écrite juste
// après celle de la commune, et répéter « % des résidences principales » en toutes lettres
// faisait déborder la ligne pour redire ce qu'elle venait de dire. Réutiliser CETTE règle
// plutôt qu'en écrire une seconde — c'est déjà celle des comparaisons de tuiles, et les
// deux blocs doivent se lire pareil.
// A12 (plan 12/09) : la règle vaut désormais pour TOUTE unité qualifiée, pas seulement
// celles en « % ». « pour 1 000 hab. de 75 ans et plus » est la même maladie que
// « % des rés. principales » : une unité qui porte son propre dénominateur en toutes
// lettres, répétée sur chacune des quatre lignes d'échelle et des trois lignes de témoin.
// Sur la tuile « Places en hébergement pour personnes âgées » de Palaiseau, elle faisait
// sortir la valeur du cadre — le débordement que Marc a signalé.
// On coupe au QUALIFICATIF (« de », « des », « du »), jamais avant : ce qui reste
// (« pour 1 000 hab. ») est l'unité, ce qui part (« de 75 ans et plus ») est la précision
// sur la population de référence — et celle-ci est écrite en toutes lettres deux lignes
// plus haut, sur la valeur de la commune, qui fixe le contexte de tout le bloc.
export function uniteComparaison(unite?: string): string | undefined {
  if (unite && unite.startsWith('%')) return '%'
  // Seuil : en dessous, l'unité tient sur la ligne et la couper ne gagnerait rien.
  if (unite && unite.length > 16) {
    const court = unite.replace(/\s+d(?:e|es|u|')\s.*$/, '').trim()
    // On n'abrège QU'UN TAUX — un début qui porte encore son dénominateur chiffré
    // (« pour 1 000 hab. ») reste une unité complète une fois la précision retirée.
    // Une unité descriptive (« nombre de logements ») serait mutilée en « nombre » :
    // la garde sur le chiffre l'écarte du traitement.
    if (court.length < unite.length && /\d/.test(court)) return court
  }
  return unite
}

// L'espace entre un nombre et son unité, dans les lignes d'échelles et de témoins (lot A
// du 08/10, #622 ; le pourquoi est écrit au-dessus de `UniteLigne`, dans `components.tsx`) :
// insécable sous 12 caractères, pour que le « % » ne parte jamais seul à la ligne ; sécable
// au-delà, pour qu'une unité longue puisse se replier. Ici et non dans `components.tsx`
// depuis le 11/10 : l'image d'une tuile écrit désormais ses témoins, et doit les écrire
// avec la même espace que l'écran.
export const UNITE_COURTE_MAX = 12
const NBSP = ' '
export const espaceAvantUnite = (unite?: string): string =>
  unite && unite.trim().length <= UNITE_COURTE_MAX ? NBSP : ' '
